import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  ArrowRightIcon,
  CheckCircleIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  GlobeAltIcon,
  HeartIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import aboutImg from "../assests/images/aboutUs.jpg";
import CTASection from "../components/CTASection";

const benefits = [
  {
    icon: MagnifyingGlassIcon,
    title: "Easy Discovery",
    description:
      "Explore accommodation options across Dubai in one convenient place.",
  },
  {
    icon: BuildingOffice2Icon,
    title: "Options for Every Trip",
    description:
      "Discover hotels for beach holidays, family breaks, and business visits.",
  },
  {
    icon: CheckCircleIcon,
    title: "Informed Decisions",
    description:
      "Review hotel details and available rates before choosing your stay.",
  },
];

const journey = [
  {
    number: "01",
    title: "Tell us what you need",
    text: "Think about your destination, travel dates, and preferred budget.",
  },
  {
    number: "02",
    title: "Explore your options",
    text: "Browse hotels and compare the available details and rates.",
  },
  {
    number: "03",
    title: "Choose your stay",
    text: "Find an option that works for your plans and travel style.",
  },
];

export default function AboutUs() {
  return (
    <>
      <Helmet>
        <title>About Us | Cheap Dubai Hotels</title>
        <meta
          name="description"
          content="Get to know Cheap Dubai Hotels. Explore Dubai accommodation, compare available hotel rates, and find a stay that suits your travel plans."
        />
        <meta
          property="og:title"
          content="About Us | Cheap Dubai Hotels"
        />
        <meta
          property="og:description"
          content="Discover a simpler way to explore hotels and plan your next stay in Dubai."
        />
      </Helmet>

      <main className="overflow-hidden bg-white text-[#062F4F]">

        {/* ==================================================
            EDITORIAL HERO
        ================================================== */}

        {/* Compact About Us Banner */}
        <section className="relative overflow-hidden bg-[#062F4F] mt-28">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${aboutImg})` }}
          />

          <div className="absolute inset-0 bg-gradient-to-r from-[#062F4F]/95 via-[#062F4F]/80 to-[#062F4F]/50" />

          <div className="relative mx-auto max-w-[1536px] px-5 py-10 sm:px-8 sm:py-12 md:py-14 lg:px-10">
            <div className="max-w-2xl">
              <span className="mb-3 inline-block rounded-full border border-[#F7B928]/40 bg-[#F7B928]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#F7B928]">
                About Cheap Dubai Hotels
              </span>

              <h1 className="text-3xl font-bold leading-tight text-white sm:text-4xl md:text-5xl">
                Your Dubai Stay{" "}
                <span className="text-[#F7B928]">Starts Here.</span>
              </h1>

              <p className="mt-3 max-w-xl text-sm leading-6 text-white/80 sm:text-base">
                Discover Dubai hotels, compare available rates, and find a stay that
                suits your travel plans and budget.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/hotels"
                  className="inline-flex items-center rounded-lg bg-[#FF5A1F] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#e94b14]"
                >
                  Explore Hotels
                </Link>

                <Link
                  to="/services"
                  className="inline-flex items-center rounded-lg border border-white/50 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  Explore Dubai
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ==================================================
            OVERLAPPING STORY PANEL
        ================================================== */}

        <section
          id="our-story"
          className="relative bg-[#F4F8FA] px-5 pb-16 sm:px-8 sm:pb-24 lg:px-12"
        >

          <div className="relative w-full max-w-[1536px] mx-auto
              px-4
            sm:px-6
            lg:px-8">

            <div className="relative grid overflow-hidden rounded-b-3xl bg-white shadow-[0_20px_65px_rgba(6,47,79,0.08)] md:grid-cols-[0.85fr_1.15fr]">

              <div className="relative min-h-[280px] md:min-h-[420px]">

                <img
                  src={aboutImg}
                  alt="A look at Dubai and its hotel experiences"
                  className="absolute inset-0 h-full w-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#062F4F]/65 via-transparent to-transparent" />

                <div className="absolute bottom-6 left-6 right-6">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-xs font-bold text-[#062F4F]">
                    <MapPinIcon className="h-4 w-4 text-[#0B6E8E]" />
                    Made for Dubai explorers
                  </span>
                </div>

              </div>

              <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">

                <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#0B6E8E]">
                  About Cheap Dubai Hotels
                </span>

                <h2 className="mt-4 text-3xl font-black leading-tight tracking-tight sm:text-4xl">
                  Finding a hotel
                  <span className="block text-[#0B6E8E]">
                    should feel simple.
                  </span>
                </h2>

                <div className="mt-5 h-1 w-14 rounded-full bg-[#F7B928]" />

                <p className="mt-6 text-sm leading-7 text-slate-600 sm:text-base">
                  Planning a trip means making lots of decisions.
                  Finding the right accommodation should not make
                  things more complicated.
                </p>

                <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-base">
                  Cheap Dubai Hotels helps visitors explore hotel
                  options around the city and compare available rates.
                  Whether you're visiting for a holiday or work, we
                  want to make your search more convenient.
                </p>

                <div className="mt-7 flex items-center gap-3 border-t border-[#E5EDF1] pt-6">

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E8F4F7] text-[#0B6E8E]">
                    <HeartIcon className="h-5 w-5" />
                  </div>

                  <div>
                    <p className="text-sm font-bold text-[#062F4F]">
                      Your trip. Your priorities.
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Discover stays that fit your plans.
                    </p>
                  </div>

                </div>

              </div>
            </div>
          </div>
        </section>

        {/* ==================================================
            DARK MISSION SECTION
        ================================================== */}

        <section className="relative overflow-hidden bg-[#062F4F] py-16 sm:py-24">

          <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full border border-white/10" />
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full border border-white/10" />
          <div className="pointer-events-none absolute -bottom-36 -left-20 h-96 w-96 rounded-full bg-[#0B6E8E]/20 blur-3xl" />

          <div className="relative w-full max-w-[1536px] mx-auto
              px-4
            sm:px-6
            lg:px-8">

            <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center lg:gap-20">

              <div>

                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#F7B928]">
                  <span className="h-[2px] w-8 bg-[#F7B928]" />
                  What Drives Us
                </span>

                <h2 className="mt-6 text-4xl font-black leading-tight !text-white sm:text-5xl">
                  Good travel
                  <span className="block text-[#F7B928]">
                    begins with
                  </span>
                  better choices.
                </h2>

                <p className="mt-6 max-w-lg text-sm leading-7 text-white/65 sm:text-base">
                  We believe finding a place to stay should be an
                  exciting part of planning your trip, not a stressful
                  one.
                </p>

              </div>

              <div className="space-y-0">

                <div className="border-b border-white/15 py-7 sm:py-8">

                  <div className="flex gap-5">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#F7B928]">
                      <SparklesIcon className="h-6 w-6" />
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Our Mission
                      </h3>

                      <p className="mt-3 text-sm leading-7 text-white/65">
                        Make hotel discovery easier by helping
                        travelers explore accommodation options and
                        compare available rates for their Dubai trip.
                      </p>
                    </div>

                  </div>
                </div>

                <div className="border-b border-white/15 py-7 sm:py-8">

                  <div className="flex gap-5">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#F7B928]">
                      <GlobeAltIcon className="h-6 w-6" />
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Our Vision
                      </h3>

                      <p className="mt-3 text-sm leading-7 text-white/65">
                        Be a helpful starting point for people
                        planning a Dubai visit, from choosing a
                        neighbourhood to discovering suitable hotels.
                      </p>
                    </div>

                  </div>
                </div>

                <div className="py-7 sm:py-8">

                  <div className="flex gap-5">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#F7B928]">
                      <HeartIcon className="h-6 w-6" />
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Our Promise
                      </h3>

                      <p className="mt-3 text-sm leading-7 text-white/65">
                        Put useful hotel information, convenient
                        discovery, and the needs of travelers at the
                        centre of the experience.
                      </p>
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* ==================================================
            BENEFITS - OFFSET EDITORIAL CARDS
        ================================================== */}

        <section className="bg-white py-16 sm:py-24">

          <div className="relative w-full max-w-[1536px] mx-auto
              px-4
            sm:px-6
            lg:px-8">

            <div className="grid gap-10 lg:grid-cols-[0.75fr_1.25fr] lg:gap-16">

              <div className="lg:sticky lg:top-28 lg:self-start">

                <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#0B6E8E]">
                  Why Cheap Dubai Hotels
                </span>

                <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">
                  Less searching.
                  <span className="block text-[#0B6E8E]">
                    More discovering.
                  </span>
                </h2>

                <p className="mt-5 text-sm leading-7 text-slate-600 sm:text-base">
                  We bring the hotel search experience into focus
                  so you can spend more time planning what you want
                  to experience in Dubai.
                </p>

                <Link
                  to="/hotels"
                  className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-[#0B6E8E] transition hover:text-[#FF5A1F]"
                >
                  Start exploring
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>

              </div>

              <div className="space-y-4">

                {benefits.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <article
                      key={item.title}
                      className="group flex gap-5 rounded-2xl border border-[#E1EAF0] bg-[#F7FAFC] p-5 transition-all duration-300 hover:border-[#0B6E8E]/40 hover:bg-white hover:shadow-[0_15px_40px_rgba(6,47,79,0.07)] sm:gap-6 sm:p-7"
                    >

                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-[#0B6E8E] shadow-sm transition-colors group-hover:bg-[#062F4F] group-hover:text-[#F7B928]">
                        <Icon className="h-6 w-6" />
                      </div>

                      <div className="flex-1">

                        <div className="flex flex-wrap items-center justify-between gap-2">

                          <h3 className="text-lg font-bold text-[#062F4F] sm:text-xl">
                            {item.title}
                          </h3>

                          <span className="text-xs font-bold tracking-widest text-[#B2C5D0]">
                            0{index + 1}
                          </span>

                        </div>

                        <p className="mt-2 text-sm leading-7 text-slate-600">
                          {item.description}
                        </p>

                      </div>

                      <ArrowRightIcon className="mt-1 hidden h-5 w-5 shrink-0 text-[#0B6E8E] transition-transform group-hover:translate-x-1 sm:block" />

                    </article>
                  );
                })}

              </div>
            </div>
          </div>
        </section>

        {/* ==================================================
            HOW IT WORKS - HORIZONTAL TIMELINE
        ================================================== */}

        <section className="relative overflow-hidden bg-[#F4F8FA] py-16 sm:py-24">

          <div className="relative w-full max-w-[1536px] mx-auto
              px-4
            sm:px-6
            lg:px-8">

            <div className="mx-auto max-w-2xl text-center">

              <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#0B6E8E]">
                Your Next Step
              </span>

              <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">
                Your hotel search,
                <span className="block text-[#0B6E8E]">
                  without the guesswork.
                </span>
              </h2>

              <p className="mt-5 text-sm leading-7 text-slate-600 sm:text-base">
                A straightforward way to start planning your
                accommodation in Dubai.
              </p>

            </div>

            <div className="relative mt-14 grid gap-8 md:grid-cols-3 md:gap-6">

              <div className="absolute left-[16%] right-[16%] top-7 hidden h-px bg-[#CFDEE6] md:block" />

              {journey.map((step) => (
                <article
                  key={step.number}
                  className="relative text-center"
                >

                  <div className="relative z-10 mx-auto flex h-14 w-14 items-center justify-center rounded-full border-4 border-[#F4F8FA] bg-[#062F4F] text-sm font-extrabold text-[#F7B928] shadow-md">
                    {step.number}
                  </div>

                  <div className="mt-5 rounded-2xl border border-[#DFE8ED] bg-white p-6 sm:p-7">

                    <h3 className="text-lg font-bold text-[#062F4F]">
                      {step.title}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-600">
                      {step.text}
                    </p>

                  </div>

                </article>
              ))}

            </div>

            <div className="mt-10 text-center">

              <Link
                to="/hotels"
                className="inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-7 py-4 text-sm font-bold text-white transition hover:bg-[#E94A12]"
              >
                Find Your Hotel
                <ArrowRightIcon className="h-4 w-4" />
              </Link>

            </div>

          </div>
        </section>

        {/* EXISTING SITE CTA */}

        <CTASection />

      </main>
    </>
  );
}